import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import { toast } from "sonner";

export type OkrExample = components["schemas"]["OkrExample"];

export function useOkrExamples() {
  return useQuery({
    queryKey: ["okr-examples"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/admin/okr-examples", {
        params: { query: { limit: 200 } },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateOkrExample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateOkrExampleRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/admin/okr-examples", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["okr-examples"] });
      toast.success("Example created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateOkrExample(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateOkrExampleRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/admin/okr-examples/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["okr-examples"] });
      toast.success("Example updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteOkrExample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/admin/okr-examples/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["okr-examples"] });
      toast.success("Example deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
